import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import AvailabilityPanel from "../../components/director/AvailabilityPanel";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import {
  compactCents,
  formatBps,
  formatCents,
  formatCivilDate,
  formatCompetenceShort,
  formatDateTimeCompact,
} from "../../utils/directorFormat";
import { companyQuery, currentCompetence, newestExtractedAt } from "./module1Utils";

const PAGE_SIZE = 25;

export const BUCKET_LABELS = {
  "1_30": "1 a 30 dias",
  "31_60": "31 a 60 dias",
  "61_90": "61 a 90 dias",
  "91_180": "91 a 180 dias",
  "180_plus": "Mais de 180 dias",
};

const BUCKET_ORDER = Object.keys(BUCKET_LABELS);
const HIDDEN_STATES = new Set(["restricted", "unavailable", "empty"]);
const NOTICE_STATES = new Set(["restricted", "unavailable", "empty", "stale", "partial"]);

const count = (value) => (Number.isSafeInteger(value) ? value.toLocaleString("pt-BR") : "—");

const HorizontalBars = ({ items, ariaLabel }) => {
  const maximum = Math.max(1, ...items.map((item) => (Number.isSafeInteger(item.value) ? Math.abs(item.value) : 0)));
  return <ul className="director-hbars" aria-label={ariaLabel}>
    {items.map((item) => <li key={item.id} data-tone={item.tone}>
      <span className="director-hbars__label">{item.label}</span>
      <span className="director-hbars__track" aria-hidden="true">
        <i style={{ "--bar-size": `${Number.isSafeInteger(item.value) && item.value > 0 ? Math.max(2, Math.round((item.value * 100) / maximum)) : 0}%` }} />
      </span>
      <strong className="director-hbars__value">{compactCents(item.value)}</strong>
      {item.note && <small className="director-hbars__note">{item.note}</small>}
    </li>)}
  </ul>;
};

const Delinquency = () => {
  const { selectedCompanyId, filters = {} } = useDirectorContext();
  const projectKey = filters.projectKey || null;
  const [pageState, setPageState] = useState({ projectKey, page: 1 });
  const page = pageState.projectKey === projectKey ? pageState.page : 1;
  const query = useMemo(() => ({ projectKey, page, pageSize: PAGE_SIZE }), [page, projectKey]);
  const surface = useDirectorSurface("delinquency", query);
  const { data } = surface;
  const summary = data?.summary || {};
  const state = data?.state || null;
  const showNumbers = data && !HIDDEN_STATES.has(state);
  const buckets = BUCKET_ORDER.map((key) => {
    const bucket = (data?.buckets || []).find((item) => item.key === key) || {};
    return {
      id: key,
      label: BUCKET_LABELS[key],
      value: bucket.overdueCents ?? 0,
      tone: key,
      note: Number.isSafeInteger(bucket.customers) ? `${count(bucket.customers)} cliente${bucket.customers === 1 ? "" : "s"}` : null,
    };
  });
  const projects = [...(data?.projects || [])]
    .sort((left, right) => (right.overdueCents ?? 0) - (left.overdueCents ?? 0))
    .map((project) => ({
      id: project.projectKey || "__without_project__",
      label: project.projectKey ? (project.name || "Obra sem nome") : "Sem obra",
      value: project.overdueCents,
      tone: "project",
    }));
  const items = data?.items || [];
  const pagination = data?.pagination || null;
  const totalPages = pagination ? Math.max(1, Math.ceil((pagination.total || 0) / (pagination.pageSize || PAGE_SIZE))) : 1;
  const freshness = Array.isArray(data?.freshness)
    ? newestExtractedAt(data.freshness)
    : (data?.freshness?.extractedAt || null);
  const goTo = (target) => setPageState({ projectKey, page: target });

  return <ModuleShell
    title="Inadimplência"
    description="Quem está devendo, quanto e há quanto tempo, a partir das parcelas a receber do ERP."
    badge={data?.asOf ? `Posição em ${formatCivilDate(String(data.asOf).slice(0, 10))}` : "Valores nominais"}
    qualitySources={[surface]}
  >
    <ModuleState {...surface} onRetry={surface.reload} />
    {data && NOTICE_STATES.has(state) && <AvailabilityPanel state={state} reasonCode={data.reasonCode} />}
    {showNumbers && <>
      <section className="director-module1__metrics director-module1__metrics--four" aria-label="Indicadores de inadimplência">
        <MetricCard
          label="Vencido e não pago"
          value={compactCents(summary.overdueCents)}
          note={`${formatBps(summary.overdueShareBps)} do que vence nos próximos 12 meses`}
          tone="pressure"
        />
        <MetricCard
          label="Clientes em atraso"
          value={count(summary.customersOverdue)}
          note={`de ${count(summary.customersTotal)} clientes com parcelas`}
          tone="orange"
        />
        <MetricCard
          label="Mais de 90 dias"
          value={compactCents(summary.over90Cents)}
          note={`${count(summary.over90Customers)} cliente${summary.over90Customers === 1 ? "" : "s"}`}
          tone="pressure"
        />
        <MetricCard
          label="Recuperado no mês"
          value={compactCents(summary.recoveredThisMonthCents)}
          note={`Parcelas vencidas pagas em ${formatCompetenceShort(currentCompetence())}`}
          tone="green"
        />
      </section>
      <section className="director-module1__split director-module1__split--even">
        <article className="director-module1__panel">
          <header><div><span className="director-eyebrow">Por tempo de atraso</span><h2>Quanto mais antigo, mais difícil de receber</h2></div></header>
          <HorizontalBars items={buckets} ariaLabel="Vencido por faixa de atraso" />
        </article>
        <article className="director-module1__panel">
          <header><div><span className="director-eyebrow">Por obra</span><h2>Onde o atraso se concentra</h2></div></header>
          {projects.length
            ? <HorizontalBars items={projects} ariaLabel="Vencido por obra" />
            : <p className="director-owner-footnote">Nenhuma obra com parcela vencida.</p>}
        </article>
      </section>
      <article className="director-module1__panel">
        <header>
          <div>
            <span className="director-eyebrow">Clientes com parcela vencida</span>
            <h2>Ordenado pelo valor vencido · {count(pagination?.total ?? items.length)} clientes</h2>
          </div>
        </header>
        <div className="director-table-scroll">
          <table className="director-table director-receivables-table director-delinquency-table" aria-label="Clientes com parcela vencida">
            <thead>
              <tr>
                <th scope="col">Cliente</th>
                <th scope="col">Obra</th>
                <th scope="col">Unidade</th>
                <th scope="col">Parcelas vencidas</th>
                <th scope="col">Valor vencido</th>
                <th scope="col">Atraso mais antigo</th>
                <th scope="col">Última baixa</th>
                <th scope="col">Faixa</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => <tr key={`${item.customerRef}-${item.unitRef || "sem-unidade"}`}>
                <th scope="row">
                  <Link
                    aria-label={`Abrir ${item.displayName || "cliente"}`}
                    to={`/dashboard/director/customer-receipts/${encodeURIComponent(item.customerRef)}${companyQuery(selectedCompanyId)}`}
                  >
                    {item.displayName || "Cliente sem nome disponível"}
                  </Link>
                </th>
                <td>{item.projectName || (item.projectKey ? "Obra sem nome" : "Sem obra")}</td>
                <td>{item.unitDisplayName || "—"}</td>
                <td>{count(item.overdueInstallments)}</td>
                <td>{formatCents(item.overdueCents)}</td>
                <td>{Number.isSafeInteger(item.daysOverdue) ? `${item.daysOverdue} dias` : "—"}</td>
                <td>{item.lastSettlementDate ? formatCivilDate(String(item.lastSettlementDate).slice(0, 10)) : "Sem baixa"}</td>
                <td><span className="director-bucket-chip" data-bucket={item.bucket}>{BUCKET_LABELS[item.bucket] || "—"}</span></td>
              </tr>)}
            </tbody>
          </table>
        </div>
        {pagination && totalPages > 1 && <nav aria-label="Paginação de clientes em atraso" className="director-pagination">
          <button aria-label="Página anterior" disabled={page <= 1} onClick={() => goTo(page - 1)} type="button">Anterior</button>
          <span>Página {page} de {totalPages}</span>
          <button aria-label="Próxima página" disabled={page >= totalPages} onClick={() => goTo(page + 1)} type="button">Próxima</button>
        </nav>}
        <p className="director-owner-footnote">
          O cliente abre o detalhe com todas as parcelas da unidade. Os dados vêm das parcelas a receber do ERP; a cobrança continua sendo feita no ERP ou pela equipe do cliente.
        </p>
      </article>
      {freshness && <footer className="director-freshness">Fontes atualizadas em {formatDateTimeCompact(freshness)}.</footer>}
    </>}
  </ModuleShell>;
};

export default Delinquency;
